// src/pages/AdminCourseImages.jsx
import React, { useMemo, useState } from "react";
import { AdminCourseImagesProvider, useAdminCourseImages } from "../contexts/AdminCourseImagesContext";
import AdminCourseImagesHeader from "../components/ui/AdminCourseImagesHeader";
import Pagination from "../components/ui/Pagination";

const formatBytes = (bytes) => {
  const b = Number(bytes || 0);
  if (b < 1024) return `${b} B`;
  const kb = b / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  return `${(kb / 1024).toFixed(2)} MB`;
};

const MAX_PENDING = 5;
const GALLERY_PAGE_SIZE = 10;

/* ── Icons ── */
const ListIcon = () => (
  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h10" />
  </svg>
);

const InfoIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);


/* ── Main inner component ── */
const AdminCourseImagesInner = () => {
  const {
    isAdmin,
    categories,
    selectedCategoryId,
    images,
    loadingCategories,
    loadingImages,
    uploading,
    isDragging,
    fileInputRef,
    setSelectedCategoryId,
    handleFileChange,
    handleDragOver,
    handleDragLeave,
    deleteImage,
  } = useAdminCourseImages();

  const [pendingFiles,    setPendingFiles]    = useState([]);
  const [pendingPreviews, setPendingPreviews] = useState([]);
  const [galleryPage,     setGalleryPage]     = useState(1);
  const [limitWarning,    setLimitWarning]    = useState(false);
  const galleryRef      = React.useRef(null);
  const prevGalleryPage = React.useRef(null);

  React.useEffect(() => {
    if (prevGalleryPage.current !== null && prevGalleryPage.current !== galleryPage) {
      galleryRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    prevGalleryPage.current = galleryPage;
  }, [galleryPage]);

  const pendingCount = pendingFiles.length;

  const galleryTotalPages = useMemo(
    () => Math.max(1, Math.ceil(images.length / GALLERY_PAGE_SIZE)),
    [images.length]
  );

  const pagedImages = useMemo(() => {
    const start = (galleryPage - 1) * GALLERY_PAGE_SIZE;
    return images.slice(start, start + GALLERY_PAGE_SIZE);
  }, [images, galleryPage]);

  const handleCategoryChange = (id) => {
    setSelectedCategoryId(id);
    setGalleryPage(1);
    clearPending();
  };

  const revokeAllPreviews = () => {
    setPendingPreviews((prev) => {
      prev.forEach((p) => { try { URL.revokeObjectURL(p.url); } catch {} });
      return [];
    });
  };

  const clearPending = () => {
    revokeAllPreviews();
    setPendingFiles([]);
    setLimitWarning(false);
  };

  const addFilesToPending = (filesList) => {
    const files = Array.from(filesList || []).filter(Boolean);
    if (!files.length) return;
    const imgFiles = files.filter((f) => (f.type || "").startsWith("image/"));
    if (!imgFiles.length) return;

    const available = MAX_PENDING - pendingCount;
    if (available <= 0) { setLimitWarning(true); return; }

    const toAdd = imgFiles.slice(0, available);
    if (imgFiles.length > available) setLimitWarning(true);

    const mapped = toAdd.map((f) => {
      const key = `${f.name}-${f.size}-${f.lastModified}-${Math.random().toString(16).slice(2)}`;
      return { key, file: f, url: URL.createObjectURL(f), name: f.name, size: f.size, type: f.type };
    });

    setPendingFiles((prev) => [...prev, ...mapped.map((m) => m.file)]);
    setPendingPreviews((prev) => [...prev, ...mapped.map(({ key, url, name, size, type }) => ({ key, url, name, size, type }))]);
  };

  const removePending = (key) => {
    setLimitWarning(false);
    setPendingPreviews((prev) => {
      const target = prev.find((p) => p.key === key);
      if (target?.url) { try { URL.revokeObjectURL(target.url); } catch {} }
      return prev.filter((p) => p.key !== key);
    });
    setPendingFiles((prev) => {
      const p = pendingPreviews.find((x) => x.key === key);
      if (!p) return prev;
      const idx = prev.findIndex((f) => f?.name === p.name && f?.size === p.size);
      if (idx === -1) return prev;
      const copy = [...prev];
      copy.splice(idx, 1);
      return copy;
    });
  };

  const uploadPending = async () => {
    if (!pendingFiles.length) return;
    await handleFileChange({ target: { files: pendingFiles, value: "" } });
    clearPending();
  };

  const onPickFiles = () => fileInputRef.current?.click();

  const onInputChangeQueueOnly = (e) => {
    const files = e.target.files;
    if (files?.length) addFilesToPending(files);
    e.target.value = "";
  };

  const onDropQueueOnly = (e) => {
    e.preventDefault();
    handleDragLeave?.();
    const files = e.dataTransfer?.files;
    if (files?.length) addFilesToPending(files);
  };

  if (!isAdmin) {
    return (
      <>
        <AdminCourseImagesHeader />
        <div className="min-h-screen max-w-7xl mx-auto p-4 sm:p-6" dir="rtl">
          <div className="text-center py-16 bg-white rounded-2xl border border-gray-200 shadow-sm">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M12 11c0 1.657-1.343 3-3 3S6 12.657 6 11s1.343-3 3-3 3 1.343 3 3zm6 10H6a4 4 0 014-4h4a4 4 0 014 4z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">אין הרשאה</h3>
            <p className="text-gray-600">עמוד זה זמין למנהלים בלבד.</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <AdminCourseImagesHeader />

      <div className="min-h-screen max-w-7xl mx-auto p-4 sm:p-6" dir="rtl">

        {/* ── Controls card ── */}
        <div className="bg-white rounded-2xl border border-gray-200 p-4 sm:p-6 md:p-8 mb-6">
          {/*
            sm+: row layout — category select at natural width, spacer, then action buttons.
            xs:  column layout — each item full-width.
          */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:flex-wrap gap-3">

            {/* Category selector */}
            <div className="relative w-full sm:w-auto sm:min-w-[200px] sm:max-w-[260px]">
              <span className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                <ListIcon />
              </span>
              <select
                className="appearance-none w-full rounded-lg border border-gray-200 bg-white pr-10 pl-4 py-2 text-sm text-gray-700 outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
                value={selectedCategoryId || ""}
                onChange={(e) => handleCategoryChange(e.target.value)}
                disabled={loadingCategories}
              >
                <option value="">{loadingCategories ? "טוען קטגוריות..." : "בחר קטגוריה"}</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>{c.name} ({c.slug})</option>
                ))}
              </select>
            </div>

            {/* Spacer — only visible on sm+ */}
            <div className="hidden sm:block flex-1" />

            {/* Action buttons — wrap naturally if they don't fit */}
            <div className="flex items-center gap-2 flex-wrap">
              {(loadingImages || uploading) && (
                <div className="flex items-center gap-2 text-gray-500">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-400 border-t-transparent" />
                  <span className="text-sm">{uploading ? "מעלה..." : "טוען..."}</span>
                </div>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={onInputChangeQueueOnly}
              />

              <button
                type="button"
                onClick={onPickFiles}
                disabled={!selectedCategoryId || uploading || pendingCount >= MAX_PENDING}
                title={pendingCount >= MAX_PENDING ? `ניתן לבחור עד ${MAX_PENDING} תמונות` : ""}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-white border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                בחר קבצים
                {pendingCount > 0 && (
                  <span className="mr-1 text-gray-400">({pendingCount}/{MAX_PENDING})</span>
                )}
              </button>

              <button
                type="button"
                onClick={uploadPending}
                disabled={!selectedCategoryId || uploading || pendingCount === 0}
                className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                העלה עכשיו{pendingCount ? ` (${pendingCount})` : ""}
              </button>

              <button
                type="button"
                onClick={clearPending}
                disabled={uploading || pendingCount === 0}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                בטל
              </button>
            </div>
          </div>

          {/* Upload limit notice */}
          <div className={`mt-3 flex items-start gap-2 text-xs rounded-lg px-3 py-2 transition-colors duration-200 ${
            limitWarning ? "bg-red-50 border border-red-200 text-red-700" : "bg-gray-50 text-gray-400"
          }`}>
            <InfoIcon className={`w-3.5 h-3.5 flex-shrink-0 mt-px ${limitWarning ? "text-red-500" : "text-gray-400"}`} />
            <span>
              {limitWarning
                ? `הגעת למגבלה — ניתן לבחור עד ${MAX_PENDING} תמונות בו זמנית. הסר תמונות מהתור כדי להוסיף נוספות.`
                : `ניתן לבחור עד ${MAX_PENDING} תמונות להעלאה בו זמנית`}
            </span>
          </div>
        </div>

        {/* ── KPI cards ── */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="bg-gradient-to-r from-indigo-50 to-blue-50 p-4 rounded-xl border border-indigo-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h10" />
                </svg>
              </div>
              <div className="min-w-0">
                <p className="text-sm text-indigo-700">קטגוריות</p>
                <p className="text-2xl font-bold text-indigo-900">{categories.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-emerald-50 to-green-50 p-4 rounded-xl border border-emerald-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <div className="min-w-0">
                <p className="text-sm text-emerald-700">תמונות בקטגוריה</p>
                <p className="text-2xl font-bold text-emerald-900">{images.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-4 rounded-xl border border-amber-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
              </div>
              <div className="min-w-0">
                <p className="text-sm text-amber-700">סטטוס</p>
                <p className="text-lg font-bold text-amber-900">
                  {uploading ? "מעלה..." : loadingImages ? "טוען..." : pendingCount ? "מוכן להעלאה" : "מוכן"}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* ── Drop zone + gallery card ── */}
        <div
          className={[
            "bg-white/80 backdrop-blur-sm rounded-2xl border shadow-sm transition-all p-4 sm:p-6",
            isDragging ? "border-indigo-400 bg-indigo-50" : "border-gray-200",
          ].join(" ")}
          onDrop={onDropQueueOnly}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          {/* Drop hint — wraps gracefully on narrow screens */}
          <div className="flex items-start sm:items-center justify-center gap-2 mb-6 text-sm text-gray-400 text-center">
            <svg className="w-5 h-5 flex-shrink-0 mt-0.5 sm:mt-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            <span>גרור תמונות לכאן לתצוגה מקדימה ואז העלה (עד {MAX_PENDING} בו זמנית)</span>
          </div>

          {/* ── Pending preview queue ── */}
          {pendingPreviews.length > 0 && (
            <div className="mb-8">
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm font-semibold text-gray-900">תצוגה מקדימה לפני העלאה</div>
                <div className="flex items-center gap-2">
                  {/* Slot dots */}
                  <div className="flex gap-1">
                    {Array.from({ length: MAX_PENDING }).map((_, i) => (
                      <div key={i} className={`w-2 h-2 rounded-full transition-colors ${i < pendingCount ? "bg-indigo-500" : "bg-gray-200"}`} />
                    ))}
                  </div>
                  <span className="text-xs text-gray-500">{pendingCount}/{MAX_PENDING}</span>
                </div>
              </div>

              {/*
                Image grid: 2 cols on xs, 3 on sm, 4 on lg, 5 on xl.
                Image card height scales: shorter on mobile (h-36), taller on sm+ (h-44 sm, h-48 lg).
              */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
                {pendingPreviews.map((p) => (
                  <div key={p.key} className="rounded-2xl border border-indigo-200 bg-indigo-50/40 overflow-hidden">
                    <div className="relative h-36 sm:h-44 lg:h-48 overflow-hidden bg-gray-100">
                      <img src={p.url} alt={p.name} className="h-full w-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removePending(p.key)}
                        className="absolute top-1.5 left-1.5 inline-flex items-center justify-center w-6 h-6 rounded-full bg-black/60 text-white text-xs hover:bg-black/80 transition"
                        title="הסר"
                      >
                        ✕
                      </button>
                      <div className="absolute top-1.5 right-1.5 px-1.5 py-0.5 rounded-full bg-amber-500 text-white text-[10px] font-bold">
                        ממתין
                      </div>
                    </div>
                    <div className="p-2">
                      <div className="text-xs font-semibold text-gray-800 truncate" title={p.name}>{p.name}</div>
                      <div className="text-[11px] text-gray-500 mt-0.5">{formatBytes(p.size)}</div>
                    </div>
                  </div>
                ))}

                {/* Empty clickable slots */}
                {pendingCount < MAX_PENDING && Array.from({ length: MAX_PENDING - pendingCount }).map((_, i) => (
                  <button
                    key={`slot-${i}`}
                    type="button"
                    onClick={onPickFiles}
                    disabled={!selectedCategoryId || uploading}
                    className="rounded-2xl border-2 border-dashed border-indigo-200 bg-indigo-50/20 h-36 sm:h-44 lg:h-48 flex flex-col items-center justify-center gap-2 text-indigo-300 hover:border-indigo-400 hover:text-indigo-500 hover:bg-indigo-50 transition disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    <span className="text-xs font-medium">הוסף</span>
                  </button>
                ))}
              </div>

              {/* Upload hint banner */}
              <div className="mt-4 flex items-start gap-2 text-xs text-gray-500 bg-amber-50 border border-amber-200 rounded-xl px-3 sm:px-4 py-2.5">
                <svg className="w-4 h-4 text-amber-500 flex-shrink-0 mt-px" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>
                  לאחר בדיקה — לחץ על{" "}
                  <span className="font-semibold text-emerald-700">"העלה עכשיו"</span> לשמירה, או{" "}
                  <span className="font-semibold text-red-600">"בטל"</span> לביטול.
                </span>
              </div>

              {images.length > 0 && (
                <div className="mt-6 border-t border-gray-200 pt-6">
                  <div className="text-sm font-semibold text-gray-700 mb-4">תמונות קיימות בקטגוריה</div>
                </div>
              )}
            </div>
          )}

          {/* ── Gallery ── */}
          {!selectedCategoryId ? (
            <div className="text-center py-12 sm:py-16">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <ListIcon />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">בחר קטגוריה</h3>
              <p className="text-gray-500 text-sm">בחר קטגוריה מהרשימה כדי להציג את התמונות שלה</p>
            </div>
          ) : loadingImages ? (
            <div className="text-center py-12 sm:py-16">
              <div className="animate-spin rounded-full h-10 w-10 border-2 border-indigo-400 border-t-transparent mx-auto mb-4" />
              <p className="text-gray-500 text-sm">טוען תמונות...</p>
            </div>
          ) : images.length === 0 ? (
            <div className="text-center py-12 sm:py-16">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">אין תמונות בקטגוריה זו</h3>
              <p className="text-gray-500 text-sm">העלה תמונות באמצעות "בחר קבצים" או גרור לכאן</p>
            </div>
          ) : (
            <>
              {/* Gallery header */}
              <div ref={galleryRef} className="flex items-center justify-between mb-3">
                <span className="text-xs text-gray-500">
                  <span className="font-semibold text-gray-700">{images.length}</span> תמונות
                  {galleryTotalPages > 1 && (
                    <> · עמוד <span className="font-semibold text-gray-700">{galleryPage}</span> / <span className="font-semibold text-gray-700">{galleryTotalPages}</span></>
                  )}
                </span>
                <span className="text-xs text-gray-400">{pagedImages.length} מוצגות</span>
              </div>

              {/* Gallery grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
                {pagedImages.map((img) => (
                  <div
                    key={img.id}
                    className="rounded-2xl border border-gray-200 overflow-hidden bg-white group hover:shadow-md hover:border-gray-300 transition-all duration-200"
                  >
                    <div className="relative h-36 sm:h-44 lg:h-48 overflow-hidden bg-gray-100">
                      <img
                        src={img.url}
                        alt={img.alt_text || ""}
                        className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <button
                        type="button"
                        onClick={() => deleteImage(img)}
                        disabled={uploading}
                        className="absolute top-1.5 left-1.5 inline-flex items-center justify-center w-6 h-6 rounded-full bg-black/60 text-white text-xs hover:bg-red-600 transition disabled:opacity-50"
                        title="מחק"
                      >
                        ✕
                      </button>
                    </div>
                    {img.alt_text && (
                      <div className="px-2 py-1.5 text-[11px] text-gray-400 truncate" title={img.alt_text}>
                        {img.alt_text}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <Pagination
                page={galleryPage}
                totalPages={galleryTotalPages}
                onPageChange={(p) => setGalleryPage(Math.min(Math.max(1, p), galleryTotalPages))}
              />
            </>
          )}
        </div>

        {/* Footer note */}
        <div className="mt-4 flex items-start gap-2 text-xs text-gray-400">
          <InfoIcon className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-400" />
          <span>עמוד זה מאפשר ניהול תמונות קורסים: בחירת קטגוריה, העלאה, תצוגה ומחיקה.</span>
        </div>
      </div>
    </>
  );
};

const AdminCourseImages = () => (
  <AdminCourseImagesProvider>
    <AdminCourseImagesInner />
  </AdminCourseImagesProvider>
);

export default AdminCourseImages;